import type { App, Component } from 'vue'

import { createApp } from '@app/adapters/create-app'

import {
  LiveInputActionsIcon,
  liveInputActionIcons,
  liveInputActionContainer,
  liveInputEvents,
} from '../constants'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {}

const getActionsOffset = (
  element?: Element
): {
  left: number
  top: number
} => {
  if (!element) {
    return { left: 0, top: 0 }
  }

  const elementRect = element.getBoundingClientRect()
  const elementLeft = elementRect.left + window.scrollX
  const elementTop = elementRect.top + window.scrollY

  return { left: elementLeft, top: elementTop }
}

const makeHandleInteractionPerTarget =
  ({
    handleCommentInteraction = noop,
    handleUndoInteraction = noop,
    handleTrashInteraction = noop,
    handleRowInteraction = noop,
    handleInsightsInteraction = noop,
  }) =>
  (action: LiveInputActionsIcon | 'live-input-action-container'): void => {
    switch (action) {
      case liveInputActionIcons.COMMENT:
        handleCommentInteraction()
        break
      case liveInputActionIcons.UNDO:
        handleUndoInteraction()
        break
      case liveInputActionIcons.TRASH:
        handleTrashInteraction()
        break
      case liveInputActionIcons.INSIGHTS:
        handleInsightsInteraction()
        break
      case liveInputActionContainer:
        handleRowInteraction()
        break
      default:
        break
    }
  }

export default ({
  LiveInputActionRow,
}: { LiveInputActionRow?: Component } = {}): {
  initialize: () => void
  mount: (
    element: HTMLElement,
    options: {
      handleCommentClick?: () => void
      handleUndoClick?: () => void
      handleTrashClick?: () => void
      handleTrashMouseEnter?: () => void
      handleTrashMouseLeave?: () => void
      hasCommentIcon?: boolean
      hasTrashIcon?: boolean
      hasUndoIcon?: boolean
      hasInsightsIcon?: boolean
      isLocked?: boolean
    }
  ) => void
  teardown: () => void
  unmount: () => void
} => {
  let delayedUnmountTimeout = 0
  let targetElement: Element | undefined = undefined
  let removeListeners = noop
  let vueComponent: App<Element> | undefined = undefined

  const handleClickOutside = (event: Event): void => {
    const target = event.target as HTMLElement
    /**
     * 1. Don't unmount the actions if the click was on the element the actions are docked to & descendants of said element
     * 2. Don't unmount the actions if the click was outside the element the actions are docked to but said element is still in focus,
     * useful for inputs that open up secondary widgets outside of the DOM hierarchy (e.g. date input widgets)
     */
    if (
      targetElement &&
      (targetElement !== target || !targetElement.contains(target)) &&
      document.activeElement !== targetElement
    ) {
      unmount()
    }
  }

  /**
   * Queue unmounting of the live input actions with a delay, useful when in preview/locked mode.
   * Entering back the actions container or the live input itself within the timeframe cancels the timeout
   */
  const queueDelayedUnmount = (): void => {
    clearTimeout(delayedUnmountTimeout)

    delayedUnmountTimeout = window.setTimeout(() => {
      unmount()
    }, 200)
  }

  const stopDelayedUnmount = (): void => {
    clearTimeout(delayedUnmountTimeout)

    delayedUnmountTimeout = 0
  }

  const initialize = (): void => {
    const container = document.querySelector(
      "[data-behaviour='live-input-actions']"
    )

    if (!container || !LiveInputActionRow) {
      return
    }

    vueComponent = createApp(
      LiveInputActionRow,
      {},
      {
        singletonId: 'live-input-actions',
      }
    )

    vueComponent.mount(container)
  }

  const mount = (
    element: Element,
    {
      handleCommentClick = noop,
      handleUndoClick = noop,
      handleTrashClick = noop,
      handleInsightsClick = noop,
      handleTrashMouseEnter = noop,
      handleTrashMouseLeave = noop,
      hasCommentIcon = false,
      hasTrashIcon = false,
      hasUndoIcon = false,
      hasInsightsIcon = false,
      isLocked = false,
    } = {}
  ): void => {
    unmount()

    targetElement = element

    window.eventHub.$emit(liveInputEvents.SHOW_ACTIONS, {
      ...getActionsOffset(targetElement),
      hasCommentIcon,
      hasTrashIcon,
      hasUndoIcon,
      hasInsightsIcon,
    })

    const handleActionClick = makeHandleInteractionPerTarget({
      handleCommentInteraction: handleCommentClick,
      handleUndoInteraction: handleUndoClick,
      handleTrashInteraction: handleTrashClick,
      handleInsightsInteraction: handleInsightsClick,
    })

    const handleMouseEnter = makeHandleInteractionPerTarget({
      handleTrashInteraction: handleTrashMouseEnter,
      handleRowInteraction: isLocked ? stopDelayedUnmount : noop,
    })

    const handleMouseLeave = makeHandleInteractionPerTarget({
      handleTrashInteraction: handleTrashMouseLeave,
      handleRowInteraction: isLocked ? queueDelayedUnmount : noop,
    })

    window.eventHub.$on(liveInputEvents.ACTION_CLICKED, handleActionClick)

    window.eventHub.$on(
      'LIVE_INPUT:CLICKED_OUTSIDE_ACTIONS',
      handleClickOutside
    )

    window.eventHub.$on(liveInputEvents.MOUSE_ENTERED_ACTION, handleMouseEnter)

    window.eventHub.$on(liveInputEvents.MOUSE_LEFT_ACTION, handleMouseLeave)

    if (targetElement && isLocked) {
      targetElement.addEventListener('mouseenter', stopDelayedUnmount)
      targetElement.addEventListener('mouseleave', queueDelayedUnmount)
    }

    removeListeners = (): void => {
      window.eventHub.$off(liveInputEvents.ACTION_CLICKED, handleActionClick)
      window.eventHub.$off(
        liveInputEvents.CLICKED_OUTSIDE_ACTIONS,
        handleClickOutside
      )
      window.eventHub.$off(
        liveInputEvents.MOUSE_ENTERED_ACTION,
        handleMouseEnter
      )
      window.eventHub.$off(liveInputEvents.MOUSE_LEFT_ACTION, handleMouseLeave)

      if (targetElement && isLocked) {
        targetElement.removeEventListener('mouseenter', stopDelayedUnmount)
        targetElement.removeEventListener('mouseleave', queueDelayedUnmount)
      }
    }
  }

  const teardown = (): void => {
    if (!vueComponent) {
      return
    }

    vueComponent.unmount()
    vueComponent = undefined
  }

  const unmount = (): void => {
    window.eventHub.$emit('LIVE_INPUT:HIDE_ACTIONS')

    targetElement = undefined

    stopDelayedUnmount()

    removeListeners()
  }

  return { initialize, mount, teardown, unmount }
}
